import { useEffect, useRef, useState } from "react";
import {
  altitudeText,
  speedText,
  headingText,
  prettyCallsign,
} from "../lib/format.js";
import { speechSupported, speak, stopSpeaking } from "../lib/speech.js";
import "./SpeakButton.css";

// Build a friendly, plain-language sentence describing the flight, suitable
// for reading aloud. We prefer clear words over jargon.
function buildSentence(aircraft, details) {
  if (!aircraft) return "";
  const info = details?.aircraft;

  const callsign = prettyCallsign(aircraft.callsign);
  const flightName =
    callsign && callsign !== "No callsign"
      ? `Flight ${callsign}`
      : "This aircraft";

  const parts = [flightName];

  // e.g. "an Airbus A320"
  const type = [info?.manufacturer, info?.model].filter(Boolean).join(" ");
  if (type) parts.push(`, ${startsWithVowel(type) ? "an" : "a"} ${type}`);

  // e.g. "operated by British Airways"
  if (info?.owner) parts.push(`, operated by ${info.owner}`);

  let sentence = parts.join("");

  if (aircraft.onGround) {
    sentence += ", is currently on the ground.";
    return sentence;
  }

  // Motion: altitude, heading, speed in plain words.
  const altitude = altitudeText(aircraft.baroAltitude, aircraft.onGround);
  const heading = spokenHeading(aircraft.heading);
  const speed = spokenSpeed(aircraft.velocity);

  const clauses = [];
  if (altitude && altitude !== "Altitude unknown") {
    clauses.push(`is flying at ${altitude}`);
  } else {
    clauses.push("is in the air");
  }
  if (heading) clauses.push(`heading ${heading}`);
  if (speed) clauses.push(`at ${speed}`);

  sentence += `, ${clauses.join(" ")}.`;
  return sentence;
}

function startsWithVowel(s) {
  return /^[aeiou]/i.test(s.trim());
}

// Turn "North-west (312°)" into a spoken "north-west".
function spokenHeading(deg) {
  const text = headingText(deg);
  if (!text || text === "Heading unknown") return "";
  return text.replace(/\s*\(.*\)\s*$/, "").toLowerCase();
}

// Turn "420 kn (483 mph)" into "420 knots".
function spokenSpeed(ms) {
  const text = speedText(ms);
  if (!text || text === "Speed unknown") return "";
  const m = text.match(/^(\d[\d,]*)\s*kn/);
  return m ? `${m[1]} knots` : "";
}

export default function SpeakButton({ aircraft, details }) {
  const [speaking, setSpeaking] = useState(false);
  const utteranceRef = useRef(null);

  // Make sure we stop speaking if this panel unmounts (e.g. flight closed).
  useEffect(() => {
    return () => {
      stopSpeaking();
    };
  }, []);

  if (!speechSupported()) return null;

  const label = speaking ? "⏹ Stop" : "🔊 Read aloud";
  const ariaLabel = speaking
    ? "Stop reading this flight aloud"
    : "Read this flight aloud";

  function handleClick() {
    if (speaking) {
      stopSpeaking();
      setSpeaking(false);
      return;
    }
    const sentence = buildSentence(aircraft, details);
    if (!sentence) return;
    const utterance = speak(sentence);
    utteranceRef.current = utterance;
    if (!utterance) return;
    setSpeaking(true);
    utterance.onend = () => setSpeaking(false);
    utterance.onerror = () => setSpeaking(false);
  }

  return (
    <button
      type="button"
      className={speaking ? "speak-button speaking" : "speak-button"}
      onClick={handleClick}
      aria-label={ariaLabel}
      aria-pressed={speaking}
    >
      {label}
    </button>
  );
}
